/* eslint-disable react/destructuring-assignment */
import React from "react"
import { useSelector } from "react-redux"

import {
    WORKFLOW_LEVEL_ASSIGNED,
    WORKFLOW_SEND_TO_LEVEL,
    WORKFLOW_RETURN_TO_LEVEL,
    WORKFLOW_APPROVED,
    WORKFLOW_RERUN,
    YES,
} from "const"
import {
    StoreType,
    UserList,
    CcApprovalLevelList,
    CcResponseList,
    LangType,
} from "client/store"
import {
    HistoryProps,
    HistoryType,
    DashIcon,
    ArrowIcon,
} from "client/components"
import { Workflow } from "./styled"

export const HistoryWorkflow = ({
    history,
}: HistoryProps) => {
    const userList = useSelector((state: StoreType) => state.user.userList)
    const levelList = useSelector((state: StoreType) => state.ccApprovalLevel.levelList)
    const responseList = useSelector((state: StoreType) => state.ccResponse.responseList)
    const lang = useSelector((state: StoreType) => state.system.lang)

    const prevUser = userList[history.prevUserId]
    const prevLevel = levelList[history.prevLevelId]
    const nextUser = userList[history.nextUserId]
    const nextLevel = levelList[history.nextLevelId]

    const prevUserName = prevUser ? `${prevUser.firstName} ${prevUser.lastName}` : history.prevUserId
    const nextUserName = nextUser ? `${nextUser.firstName} ${nextUser.lastName}` : history.nextUserId

    if (history.workflow === WORKFLOW_LEVEL_ASSIGNED) {
        if (!prevLevel) return null // level was removed
        const workflow = lang.workflowLevelAssigned.replace("{level}", prevLevel.name.toString())
        return history.prevUserId
            ? (
                <Workflow>
                    {`${workflow}: ${prevUserName}`}
                    <ArrowIcon />
                    {nextUserName}
                </Workflow>
            ) : (
                <Workflow>
                    {`${workflow}: ${nextUserName}`}
                </Workflow>
            )
    }

    if (history.workflow === WORKFLOW_SEND_TO_LEVEL) {
        if (!nextLevel) return null
        const workflow = lang.workflowSendToLevel.replace("{level}", nextLevel.name.toString())
        return history.nextUserId
            ? (
                <Workflow>
                    {workflow}
                    <DashIcon />
                    {nextUserName}
                </Workflow>
            ) : (
                <Workflow>
                    {workflow}
                </Workflow>
            )
    }

    if (history.workflow === WORKFLOW_RETURN_TO_LEVEL) {
        if (!nextLevel) return null
        const workflow = lang.workflowReturnToLevel.replace("{level}", nextLevel.name.toString())
        return history.nextUserId
            ? (
                <Workflow>
                    {workflow}
                    <DashIcon />
                    {nextUserName}
                </Workflow>
            ) : (
                <Workflow>
                    {workflow}
                </Workflow>
            )
    }

    if (history.workflow === WORKFLOW_APPROVED) {
        const response = responseList[history.responseId]
        return (
            <Workflow>
                {
                    response && response.approved === YES
                        ? lang.workflowApproved
                        : lang.workflowNotApproved
                }
            </Workflow>
        )
    }

    if (history.workflow === WORKFLOW_RERUN) {
        return (
            <Workflow>
                {lang.workflowRerun}
            </Workflow>
        )
    }

    return null
}

export const HistoryWorkflowPdfExport = (
    history: HistoryType,
    userList: UserList,
    levelList: CcApprovalLevelList,
    responseList: CcResponseList,
    lang: LangType,
) => {
    const prevUser = userList[history.prevUserId]
    const prevLevel = levelList[history.prevLevelId]
    const nextUser = userList[history.nextUserId]
    const nextLevel = levelList[history.nextLevelId]

    const prevUserName = prevUser ? `${prevUser.firstName} ${prevUser.lastName}` : history.prevUserId
    const nextUserName = nextUser ? `${nextUser.firstName} ${nextUser.lastName}` : history.nextUserId

    if (history.workflow === WORKFLOW_LEVEL_ASSIGNED) {
        if (!prevLevel) return null
        const workflow = lang.workflowLevelAssigned.replace("{level}", prevLevel.name.toString())
        return history.prevUserId
            ? ({ text: `${workflow}: ${prevUserName} —> ${nextUserName}` })
            : ({ text: `${workflow}: ${nextUserName}` })
    }

    if (history.workflow === WORKFLOW_SEND_TO_LEVEL) {
        if (!nextLevel) return null
        const workflow = lang.workflowSendToLevel.replace("{level}", nextLevel.name.toString())
        return history.nextUserId
            ? ({ text: `${workflow} — ${nextUserName}` })
            : ({ text: workflow })
    }

    if (history.workflow === WORKFLOW_RETURN_TO_LEVEL) {
        if (!nextLevel) return null
        const workflow = lang.workflowReturnToLevel.replace("{level}", nextLevel.name.toString())
        return history.nextUserId
            ? ({ text: `${workflow} — ${nextUserName}` })
            : ({ text: workflow })
    }

    if (history.workflow === WORKFLOW_APPROVED) {
        const response = responseList[history.responseId]
        return ({
            text: response && response.approved === YES
                ? lang.workflowApproved
                : lang.workflowNotApproved,
        })
    }

    if (history.workflow === WORKFLOW_RERUN) {
        return ({ text: lang.workflowRerun })
    }

    return null
}
